import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Apple, BarChart3, Boxes, ClipboardCheck, Settings, Truck, Warehouse } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import harvestService from '../../services/harvest';
import type { HarvestSettings } from '../../types/harvest';
import ReceivePanel from '../../components/harvest/ReceivePanel';
import BinsPanel from '../../components/harvest/BinsPanel';
import HarvestSettingsPanel from '../../components/harvest/HarvestSettingsPanel';
import LoadsPanel from '../../components/harvest/LoadsPanel';
import PackoutsPanel from '../../components/harvest/PackoutsPanel';
import PackoutReportPanel from '../../components/harvest/PackoutReportPanel';

type TabKey = 'receive' | 'loads' | 'packouts' | 'report' | 'bins' | 'settings';
const TABS: { key: TabKey; label: string; icon: ReactNode }[] = [
  { key: 'receive', label: 'Receive & label', icon: <Warehouse className="h-4 w-4" /> },
  { key: 'loads', label: 'Loads', icon: <Truck className="h-4 w-4" /> },
  { key: 'packouts', label: 'Packouts', icon: <ClipboardCheck className="h-4 w-4" /> },
  { key: 'report', label: 'Packout report', icon: <BarChart3 className="h-4 w-4" /> },
  { key: 'bins', label: 'All bins', icon: <Boxes className="h-4 w-4" /> },
  { key: 'settings', label: 'Settings', icon: <Settings className="h-4 w-4" /> },
];

/**
 * /harvest/shed — where chalked bins become tagged ones. Gated in App.tsx on a
 * farm_jobs supervisor / creator / admin seat.
 */
export default function Shed() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab') as TabKey | null;
  const tab: TabKey = TABS.some(t => t.key === requested) ? (requested as TabKey) : 'receive';
  const { hasAccess: isAdmin } = useModuleLicense('farm_jobs', 'admin');
  const [settings, setSettings] = useState<HarvestSettings | null>(null);
  const load = useCallback(() => { harvestService.getSettings().then(setSettings).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  // "Enter packout" on a load hands its id to the Packouts tab.
  const [packoutForLoad, setPackoutForLoad] = useState<number | null>(null);
  const clearPackoutForLoad = useCallback(() => setPackoutForLoad(null), []);

  return (
    <>
      <PageHeader title="Shed" subtitle="Receive bins, send loads, record packouts" icon={<Warehouse className="h-6 w-6 text-white" />}
        actionButtons={<Button variant="outline" size="sm" onClick={() => navigate('/harvest/pick')}><Apple className="h-4 w-4" /> Start bins</Button>} />
      <div className="max-w-5xl mx-auto px-4 py-6">
        <Tabs tabs={TABS} active={tab} onChange={k => setParams(k === 'receive' ? {} : { tab: k }, { replace: true })} className="mb-4" />
        <div className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6">
          {tab === 'receive' && <ReceivePanel settings={settings} />}
          {tab === 'loads' && <LoadsPanel onEnterPackout={id => { setPackoutForLoad(id); setParams({ tab: 'packouts' }, { replace: true }); }} />}
          {tab === 'packouts' && <PackoutsPanel startForLoad={packoutForLoad} onStarted={clearPackoutForLoad} />}
          {tab === 'report' && <PackoutReportPanel />}
          {tab === 'bins' && <BinsPanel />}
          {tab === 'settings' && <HarvestSettingsPanel settings={settings} canEdit={isAdmin} onChanged={load} />}
        </div>
      </div>
    </>
  );
}
